import { useState } from 'react';
import { textGeneratorTool, imageGeneratorTool, videoCreatorTool } from '../../mocks/aiTools';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

// Placeholder affiliate links - will be replaced with real ones
const PLACEHOLDER_LINK = '#';

interface ToolCardProps {
  name: string;
  tagline: string;
  description: string;
  icon: string;
  opportunities: string[];
  guide: string[];
  ctaText: string;
  link: string;
}
function ToolCard({ name, tagline, description, icon, opportunities, guide, ctaText, link }: ToolCardProps) {

  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:-translate-y-1 transition-all duration-300 flex flex-col">
      <div className="bg-darkBlue-500 p-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 flex items-center justify-center bg-clairBlue-300/20 rounded-xl">
            <i className={`${icon} text-clairBlue-300 text-2xl`}></i>
          </div>
          <div>
            <h3 className="font-display text-xl font-bold text-white">{name}</h3>
            <p className="text-clairBlue-200 text-xs">{tagline}</p>
          </div>
        </div>
      </div>
      <div className="p-6 flex flex-col flex-1">
        <p className="text-gray-500 text-sm leading-relaxed mb-4">{description}</p>
        
        <div className="mb-4">
          <h4 className="font-bold text-darkBlue-500 text-sm mb-2">💰 Money-Making Opportunities:</h4>
          <ul className="space-y-1.5 mb-4">
            {opportunities.map((opp, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                <div className="w-4 h-4 flex items-center justify-center rounded-full bg-clairBlue-50 flex-shrink-0 mt-0.5">
                  <i className="ri-check-line text-clairBlue-500 text-xs"></i>
                </div>
                {opp}
              </li>
            ))}
          </ul>
        </div>

        <div className="mb-6 flex-1">
          <h4 className="font-bold text-darkBlue-500 text-sm mb-2">📋 Step-by-Step Guide:</h4>
          <ol className="space-y-2">
            {guide.map((step, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-darkBlue-500 text-white text-xs flex-shrink-0 mt-0.5">{i + 1}</span>
                {step}
              </li>
            ))}
          </ol>
        </div>
        <a
          href={link}
          target="_blank"
          rel="nofollow"
          className="w-full flex items-center justify-center gap-2 bg-clairBlue-300 hover:bg-clairBlue-200 text-darkBlue-500 font-bold text-sm px-6 py-3 rounded-xl transition-all hover:scale-105"
        >
          {ctaText}
          <i className="ri-external-link-line"></i>
        </a>
      </div>
    </div>
  );
}

export default function AIMakeMoneyPage() {
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [subscribed, setSubscribed] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.name && formData.email) {
      setSubscribed(true);
    }
  };

  const tools: ToolCardProps[] = [
    {
      name: 'AI Text Generator - Jasper AI',
      tagline: 'Create high-quality content quickly',
      description: 'Create high-quality blogs, ads, and product descriptions quickly using AI. Perfect for freelancers and content creators.',
      icon: 'ri-quill-pen-line',
      opportunities: [
        'Write blog posts for clients',
        'Create ad copy for businesses',
        'Generate SEO content',
        'Produce social media posts',
      ],
      guide: [
        'Sign up for Jasper AI',
        'Choose your content type',
        'Enter your topic/keywords',
        'Generate and edit content',
        'Deliver to clients',
      ],
      ctaText: 'Try Jasper AI',
      link:"https://api.whatsapp.com/send/?phone=256773515447&text&type=phone_number&app_absent=0",
    },
    {
      name: 'AI Image Generator - PixelDojo',
      tagline: 'Generate stunning images instantly',
      description: 'Generate stunning images, social media graphics, and digital art using AI tools in seconds.',
      icon: 'ri-image-ai-line',
      opportunities: [
        'Design logos for startups',
        'Create social media graphics',
        'Build brand assets',
        'Generate product mockups',
      ],
      guide: [
        'Join PixelDojo platform',
        'Write your prompt',
        'Generate variations',
        'Upscale for quality',
        'Use for clients',
      ],
      ctaText: 'Try PixelDojo',
      link:"https://whop.com/e/trk_LF4MaXpaj2OA3I/swellscan",
    },
    {
      name: 'AI Video Generator - Pictory',
      tagline: 'Turn text into engaging videos',
      description: 'Turn text into engaging videos for YouTube, TikTok, and marketing with AI-powered video creation.',
      icon: 'ri-video-ai-line',
      opportunities: [
        'Create YouTube videos',
        'Make TikTok content',
        'Produce marketing videos',
        'Turn blog posts into videos',
      ],
      guide: [
        'Create your Pictory account',
        'Paste your script',
        'Choose voice & style',
        'Customize branding',
        'Export and monetize',
      ],
      ctaText: 'Create Videos with AI',
      link:"https://api.whatsapp.com/send/?phone=256773515447&text&type=phone_number&app_absent=0",
    },
    {
      name: 'AI Voice Generator - Murf AI',
      tagline: 'Generate realistic voiceovers',
      description: 'Generate realistic voiceovers for videos, ads, and presentations without recording your own voice.',
      icon: 'ri-mic-line',
      opportunities: [
        'Voiceover for videos',
        'Create podcast intros',
        'Produce ads with professional voice',
        ' narration for e-learning',
      ],
      guide: [
        'Sign up for Murf AI',
        'Choose voice type',
        'Enter your script',
        'Customize pitch and tone',
        'Download and use',
      ],
      ctaText: 'Try Voice AI',
      link:"https://api.whatsapp.com/send/?phone=256773515447&text&type=phone_number&app_absent=0",
    },
    {
      name: 'AI Marketing Tools - Systeme.io',
      tagline: 'Build sales funnels & automate marketing',
      description: 'Build sales funnels, automate marketing, and grow your online business using one powerful platform.',
      icon: 'ri-line-chart-line',
      opportunities: [
        'Create automated sales funnels',
        'Set up email marketing campaigns',
        'Launch online courses',
        'Manage affiliate programs',
      ],
      guide: [
        'Create Systeme.io account',
        'Build your first funnel',
        'Set up email automation',
        'Integrate payments',
        'Launch and scale',
      ],
      ctaText: 'Start Marketing with AI',
      link:"https://api.whatsapp.com/send/?phone=256773515447&text&type=phone_number&app_absent=0",
    },
    {
      name: 'AI Design Tools - Canva',
      tagline: 'Design anything with AI',
      description: 'Design social media posts, branding materials, and visuals easily with beginner-friendly AI tools.',
      icon: 'ri-palette-line',
      opportunities: [
        'Create social media graphics',
        'Design logos and branding',
        'Build presentations',
        'Design marketing materials',
      ],
      guide: [
        'Sign up for Canva',
        'Choose a template',
        'Use AI design features',
        'Customize with your brand',
        'Export and share',
      ],
      ctaText: 'Start Designing Now',
      link:"https://api.whatsapp.com/send/?phone=256773515447&text&type=phone_number&app_absent=0",
    },
  ];

  return (
    <div className="font-sans">
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-darkBlue-500">
          <div className="max-w-7xl mx-auto px-4 lg:px-8 text-center">
            <h1 className="font-display text-3xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              How to Make Money Online Using the Best AI Tools in 2026
            </h1>
            <p className="text-gray-300 text-lg lg:text-xl max-w-3xl mx-auto leading-relaxed">
              AI tools are transforming the way people create content, design visuals, and produce videos. Discover top AI tools and step-by-step ways to monetize them.
            </p>
          </div>
        </section>

        {/* Top AI Tools Section */}
        <section className="py-20 bg-brokenWhite-100">
          <div className="max-w-7xl mx-auto px-4 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-display text-2xl lg:text-4xl font-bold text-darkBlue-500 mb-4">
                Top AI Tools to Make Money
              </h2>
              <p className="text-gray-500 text-lg max-w-2xl mx-auto">
                These powerful AI tools can help you build a profitable online business. Learn how to use each one to generate income.
              </p>
              <p className="text-clairBlue-600 font-semibold text-lg mt-4">
                Choose the best AI tools below and start making money online today.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {tools.map((tool) => (
                <ToolCard key={tool.name} {...tool} />
              ))}
            </div>
          </div>
        </section>

        {/* How to Make Money Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-display text-2xl lg:text-4xl font-bold text-darkBlue-500 mb-4">
                How to Make Money with AI Tools
              </h2>
              <p className="text-gray-500 text-lg max-w-2xl mx-auto">
                Proven strategies to start earning online using AI tools - even as a beginner.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {/* Freelancing */}
              <div className="bg-brokenWhite-100 rounded-2xl p-8 border border-gray-100">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-clairBlue-300/20 rounded-xl">
                    <i className="ri-briefcase-3-line text-clairBlue-500 text-xl"></i>
                  </div>
                  <h3 className="font-display text-xl font-bold text-darkBlue-500">1. Freelancing with AI Tools</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-clairBlue-500 mt-1"></i>
                    Offer content writing services on Upwork, Fiverr, or Freelancer
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-clairBlue-500 mt-1"></i>
                    Provide logo and graphic design services
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-clairBlue-500 mt-1"></i>
                    Create video content for YouTubers and businesses
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-clairBlue-500 mt-1"></i>
                    Charge $50-500+ per project depending on complexity
                  </li>
                </ul>
              </div>

              {/* Content Creation */}
              <div className="bg-brokenWhite-100 rounded-2xl p-8 border border-gray-100">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-clairBlue-300/20 rounded-xl">
                    <i className="ri-video-upload-line text-clairBlue-500 text-xl"></i>
                  </div>
                  <h3 className="font-display text-xl font-bold text-darkBlue-500">2. Content Creation</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-clairBlue-500 mt-1"></i>
                    Start a YouTube channel and monetize with ads
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-clairBlue-500 mt-1"></i>
                    Build a blog and earn through affiliate marketing
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-clairBlue-500 mt-1"></i>
                    Grow Instagram/TikTok for brand deals
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-clairBlue-500 mt-1"></i>
                    AI helps you produce 10x more content in less time
                  </li>
                </ul>
              </div>

              {/* Digital Products */}
              <div className="bg-brokenWhite-100 rounded-2xl p-8 border border-gray-100">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-clairBlue-300/20 rounded-xl">
                    <i className="ri-shopping-bag-3-line text-clairBlue-500 text-xl"></i>
                  </div>
                  <h3 className="font-display text-xl font-bold text-darkBlue-500">3. Selling Digital Products</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-clairBlue-500 mt-1"></i>
                    Create and sell e-books on Amazon or Gumroad
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-clairBlue-500 mt-1"></i>
                    Design templates for businesses
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-clairBlue-500 mt-1"></i>
                    Build online courses on Skillshare or Udemy
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-clairBlue-500 mt-1"></i>
                    Sell print-on-demand products with AI designs
                  </li>
                </ul>
              </div>

              {/* Tips */}
              <div className="bg-brokenWhite-100 rounded-2xl p-8 border border-gray-100">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-clairBlue-300/20 rounded-xl">
                    <i className="ri-star-smile-line text-clairBlue-500 text-xl"></i>
                  </div>
                  <h3 className="font-display text-xl font-bold text-darkBlue-500">4. Tips to Maximize Earnings</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-clairBlue-500 mt-1"></i>
                    Focus on one tool initially - master it before moving on
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-clairBlue-500 mt-1"></i>
                    Build a portfolio to attract higher-paying clients
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-clairBlue-500 mt-1"></i>
                    Reinvest earnings into paid tools and advertising
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-clairBlue-500 mt-1"></i>
                    Stay consistent - AI makes it easier to produce content regularly
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Why Use AI Section */}
        <section className="py-20 bg-darkBlue-500">
          <div className="max-w-7xl mx-auto px-4 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-display text-2xl lg:text-4xl font-bold text-white mb-4">
                Why Use AI Tools to Make Money?
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center bg-clairBlue-300/20 rounded-2xl">
                  <i className="ri-time-line text-clairBlue-300 text-2xl"></i>
                </div>
                <h3 className="text-white font-bold text-xl mb-3">Save Time</h3>
                <p className="text-gray-400 text-sm">Create content in minutes instead of hours. AI automates repetitive tasks so you can focus on growing your business.</p>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center bg-clairBlue-300/20 rounded-2xl">
                  <i className="ri-trending-up-line text-clairBlue-300 text-2xl"></i>
                </div>
                <h3 className="text-white font-bold text-xl mb-3">Increase Income</h3>
                <p className="text-gray-400 text-sm">Take on more clients and projects by working faster. Scale your income without working more hours.</p>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center bg-clairBlue-300/20 rounded-2xl">
                  <i className="ri-rocket-line text-clairBlue-300 text-2xl"></i>
                </div>
                <h3 className="text-white font-bold text-xl mb-3">Easy to Start</h3>
                <p className="text-gray-400 text-sm">No technical skills required. Our guides help you get started step-by-step, even as a complete beginner.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-clairBlue-300">
          <div className="max-w-7xl mx-auto px-4 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="font-display text-2xl lg:text-4xl font-bold text-darkBlue-500 mb-4">
                Start using AI tools and grow your income today
              </h2>
              <p className="text-darkBlue-400 text-lg">
                Choose the best AI tools below and start making money online today.
              </p>
            </div>

            {/* Email Capture Form */}
            <div className="max-w-md mx-auto bg-white rounded-2xl p-8 shadow-lg">
              {subscribed ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-green-100 rounded-full">
                    <i className="ri-check-line text-green-500 text-3xl"></i>
                  </div>
                  <h3 className="font-display text-xl font-bold text-darkBlue-500 mb-2">Thank You!</h3>
                  <p className="text-gray-500 text-sm">Check your email for the free AI tools guide.</p>
                </div>
              ) : (
                <>
                  <h3 className="font-display text-xl font-bold text-darkBlue-500 mb-2 text-center">Get Free AI Tools Guide</h3>
                  <p className="text-gray-500 text-sm mb-6 text-center">Join thousands of others learning to make money with AI.</p>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <input
                        type="text"
                        placeholder="Your Name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full bg-brokenWhite-100 border border-gray-200 rounded-xl px-4 py-3 text-sm text-darkBlue-500 focus:outline-none focus:border-clairBlue-500"
                        required
                      />
                    </div>
                    <div>
                      <input
                        type="email"
                        placeholder="Your Email Address"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full bg-brokenWhite-100 border border-gray-200 rounded-xl px-4 py-3 text-sm text-darkBlue-500 focus:outline-none focus:border-clairBlue-500"
                        required
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full bg-darkBlue-500 hover:bg-darkBlue-600 text-white font-bold text-sm py-3 rounded-xl transition-all hover:scale-105"
                    >
                      Get Free Guide
                    </button>
                  </form>
                </>
              )}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap justify-center gap-4 mt-10">
              <a
                href="https://www.bientomart.com/ai-tools?tab=text"
                target="_blank"
                rel="nofollow"
                className="flex items-center justify-center gap-2 bg-darkBlue-500 hover:bg-darkBlue-600 text-white font-bold text-sm px-8 py-4 rounded-xl transition-all hover:scale-105"
              >
                Explore AI Tools
                <i className="ri-external-link-line"></i>
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}